/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/ban-types */
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  SearchEngine,
  buildResultList,
  ResultListState,
  ResultList,
  loadAdvancedSearchQueryActions,
  buildResultsPerPage,
  loadPaginationActions,
} from '@coveo/headless';
import { setCoveoResult } from 'src/features/coveoSlice';
import { dayOfBirthToAge, getStateFromZipcode } from 'lib/utils/coveoHeadless';
import { setContext } from 'lib/utils/coveo/headlessContext';
import { RootState } from 'src/store/store';
import { isEditorActive, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

const FIELDS_TO_INCLUDE: string[] = [
  'trialminage',
  'trialmaxage',
  'trialphase',
  'trialcompletiondate',
  'trialenrollmenttype',
  'trialstatus',
  'trialenrollment',
  'trialorgstudyid',
  'isvirtual',
  'trialenrollingstatus',
  'trialname',
  'isfeaturedtrial',
  'featuredeprurl',
];

const useResultList = (
  engine: SearchEngine<{}>,
  zipCode: string,
  dayOfBirth: string,
  location: any,
  resultsPerPage: number
): { state?: ResultListState; fetchMoreResults: () => void } => {
  const dispatch = useDispatch();
  const [state, setState] = useState<ResultListState>();
  const resultListRef = useRef<ResultList | null>(null);
  const userProfile = useSelector((state: RootState) => state.crProfile);
  const profileDetails = userProfile?.profileData?.basicDetails;
  const age = dayOfBirthToAge(dayOfBirth ?? String(new Date(profileDetails?.dob).getFullYear()));
  const { distance, showAnyLocation, isVirtual } = useSelector(
    (state: RootState) => state.coveoResult
  );
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const isProfile = Object.keys(userProfile?.profileData).length !== 0;
  const [locationStateByZipCode, setLocationStateByZipCode] = useState<{
    latitude?: number;
    longitude?: number;
  }>();
  const { sitecoreContext } = useSitecoreContext();

  useEffect(() => {
    if (!isProfile && !zipCode && location) {
      setLocationStateByZipCode({ latitude: location?.latitude, longitude: location?.longitude });
    } else if (!zipCode && location) {
      setLocationStateByZipCode({ latitude: location?.latitude, longitude: location?.longitude });
    }
  }, [isProfile, location, location?.latitude, location?.longitude, zipCode]);

  const fetchLocationData = async () => {
    // const zipCodeValue =
    //   isProfile && profileDetails?.zipCode && !zipCode ? profileDetails?.zipCode : zipCode;

    // if (!zipCodeValue) return;

    // Clearing lat, long incase if user clears zipcode
    const zipCodeValue = zipCode;
    if (!zipCodeValue && !location) {
      setLocationStateByZipCode({
        latitude: undefined,
        longitude: undefined,
      });
      return;
    }

    if (zipCodeValue) {
      try {
        const stateFromZipCode: any = await getStateFromZipcode(zipCodeValue);
        if (stateFromZipCode) {
          setLocationStateByZipCode({
            latitude: stateFromZipCode.latitude,
            longitude: stateFromZipCode.longitude,
          });
        }
      } catch (_error) {
        setLocationStateByZipCode({
          latitude: undefined,
          longitude: undefined,
        });
      }
    }
  };

  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google) {
        setIsGoogleMapsLoaded(true);
      } else {
        setTimeout(checkGoogleMaps, 100);
      }
    };

    checkGoogleMaps();
  }, []);

  useEffect(() => {
    if (isGoogleMapsLoaded) {
      fetchLocationData();
    }
  }, [isGoogleMapsLoaded, isProfile, profileDetails?.zipCode, userProfile.profileData, zipCode]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    if (typeof window === 'undefined') return;
    const { registerPage } = loadPaginationActions(engine);
    registerPage(1);
    const searchQueryActionCreators = loadAdvancedSearchQueryActions(engine);
    const ageQueryValue = age ? ` @maxage>=${age} AND @minage<=${age}` : '';
    const trialLocationGovSource =
      process.env.NEXT_PUBLIC_COVEO_CTGOV_LOCATION_SOURCE || 'Trial Location Gov Dev';
    const trialLocationSource =
      process.env.NEXT_PUBLIC_COVEO_CTMS_LOCATION_SOURCE || 'Trial Locations Dev';
    const trialSource = process.env.NEXT_PUBLIC_COVEO_TRIAL_SOURCE || 'Trial Locations Dev';
    const virtualQuery = isVirtual ? ` @isvirtual=1` : '';
    const locationQueryValue = !showAnyLocation
      ? ` @trialorgstudyid=[[@trialorgstudyid] @source=("${trialLocationSource}" OR "${trialLocationGovSource}") @documenttype=Location $qf(function: 'dist(@triallatitude, @triallongitude, ${locationStateByZipCode?.latitude}, ${locationStateByZipCode?.longitude})*0.000621371', fieldName: 'distance') @distance<=${distance}]${virtualQuery}`
      : ` @trialorgstudyid=[[@trialorgstudyid] @source=("${trialLocationSource}" OR "${trialLocationGovSource}") @documenttype=Location]${virtualQuery}`;

    const advancedSearchQueriesOptions: { aq: string } = {
      aq: `@source=${trialSource}${ageQueryValue}${locationQueryValue}`,
    };

    const searchQueryAction = searchQueryActionCreators.updateAdvancedSearchQueries(
      advancedSearchQueriesOptions
    );

    engine.dispatch(searchQueryAction);

    const userInterests = userProfile?.profileData.conditions?.map(
      (item: any) => item.conditionValue
    );
    setContext(
      userProfile && {
        gender: profileDetails?.gender,
        dateOfBirth: profileDetails?.dob,
        interests: userInterests,
      }
    );

    const resultList: ResultList = buildResultList(engine, {
      options: {
        fieldsToInclude: FIELDS_TO_INCLUDE,
      },
    });

    resultListRef.current = resultList;

    setState(resultList.state);
    dispatch(setCoveoResult(resultList.state));

    resultList.subscribe(() => {
      setState(resultList.state);
    });

    return () => {
      resultList.subscribe(() => {});
    };
  }, [
    age,
    dayOfBirth,
    dispatch,
    profileDetails?.dob,
    engine,
    profileDetails?.gender,
    userProfile,
    zipCode,
    showAnyLocation,
    distance,
    locationStateByZipCode?.latitude,
    locationStateByZipCode?.longitude,
    isVirtual,
  ]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    const pagePerResults = buildResultsPerPage(engine, {
      initialState: { numberOfResults: 15000 },
    });
    pagePerResults.set(resultsPerPage);
  }, [engine, resultsPerPage]);

  const fetchMoreResults = () => {
    if (resultListRef.current) {
      resultListRef.current.fetchMoreResults();
    }
  };

  return { state, fetchMoreResults };
};

export default useResultList;
